import React, { memo } from 'react';
import {
  TableCell, Box,
  Stack,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import { UsageSummaryPill } from '../../../components/shared/orders/UsageSummaryPill';

import { applyRoundAndFormat, getInventorySku } from '../../../utils';
import Tooltip from '@mui/material/Tooltip';
import { PredictedDemandModel } from '../../../app/services/api/podClient';
import './OrderForm.css';
import { OrderFormItem, DeliveryDates, OrderFormItemUpdate } from '../../../app/services/api/orderManagementClient';
import QuantityCell from './QuantityCell';
import { isSameDayOrderForm } from '../utils';


interface OrderFormRowProps {
  item: OrderFormItem;
  dates: DeliveryDates[];
  handleSingleQuantityChange: (
    newQuantity: number | null,
    sku: string,
    itemId: number,
    dateIndex: number
  ) => Promise<void>;
  canSaveOrder: boolean;
  isQuantityError: (item: OrderFormItem, currentValue: string) => boolean;
  quantityError: (item: OrderFormItem, currentValue: string) => string | null;
  showMessageWhenNoCost: boolean;
  currencyCode: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  predictedDemands: PredictedDemandModel[];
  orderType: number;
  handleMagicWandClicked: (predictedQuantity: number, item: OrderFormItem, index: number) => void;
  canEditPredictions: boolean;
  upsertOrderFormItemUpdate: (single: OrderFormItemUpdate) => void;
  getItemUpdate: (item: OrderFormItem) => OrderFormItemUpdate | undefined;
  isAlternateItemCardEnabled: boolean;
  isParEnabled?: boolean;
}

const hasText = (value?: string | null): value is string => typeof value === 'string' && value.trim().length > 0;

const isValidNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const parseNumberLike = (value: unknown): number | null => {
  if (isValidNumber(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.replace(/,/g, '').trim();
    if (normalizedValue.length === 0) {
      return null;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const joinTextParts = (parts: Array<string | null | undefined>, separator = ' ') => {
  return parts.filter((part): part is string => hasText(part ?? '')).join(separator).trim();
};

const formatNumericValue = (value: number) => {
  if (Number.isInteger(value)) {
    return value.toString();
  }

  return value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

const getOrderableConversionCount = (item: OrderFormItem): number | null => {
  const candidates = [
    parseNumberLike(item.packSize),
    parseNumberLike(item.quantityInUom),
    parseNumberLike(item.caseSize),
  ];

  const conversionCount = candidates.find((candidate) => isValidNumber(candidate) && candidate > 0);
  return conversionCount ?? null;
};

const formatConversionDisplay = (count: number | null, primaryUom?: string | null, orderableUom?: string | null) => {
  if (count == null || !hasText(primaryUom) || !hasText(orderableUom)) {
    return null;
  }

  if (Math.abs(count - 1) < 0.0001) {
    return null;
  }

  return `${formatNumericValue(count)} ${primaryUom.trim()}`;
};

const formatCostDisplay = (price: number, currencyCode: string, showMessageWhenNoCost: boolean) => {
  if (showMessageWhenNoCost || !isValidNumber(price) || price <= 0) {
    return null;
  }

  return hasText(currencyCode)
    ? applyRoundAndFormat(price, currencyCode)
    : formatNumericValue(price);
};

const formatOrderLine = (
  orderLabel: string,
  costDisplay: string | null,
  orderableUom?: string | null,
  conversionDisplay?: string | null,
) => {
  const cleanOrderableUom = hasText(orderableUom) ? orderableUom.trim() : null;

  if (!costDisplay && !cleanOrderableUom) {
    return null;
  }

  const baseText = costDisplay && cleanOrderableUom
    ? `${joinTextParts([orderLabel, costDisplay])} / ${cleanOrderableUom}`
    : joinTextParts([orderLabel, costDisplay ?? cleanOrderableUom]);

  if (!hasText(baseText)) {
    return null;
  }

  return conversionDisplay ? `${baseText} (${conversionDisplay})` : baseText;
};

const formatStockLine = (
  label: string,
  quantity: unknown,
  primaryUom?: string | null,
  convertedQuantity?: number | null,
  convertedUom?: string | null,
) => {
  const numericQuantity = parseNumberLike(quantity);

  if (numericQuantity == null) {
    return null;
  }

  const primaryDisplay = joinTextParts([formatNumericValue(numericQuantity), hasText(primaryUom) ? primaryUom.trim() : null]);

  if (!hasText(primaryDisplay)) {
    return null;
  }

  const convertedDisplay = isValidNumber(convertedQuantity) && hasText(convertedUom)
    ? joinTextParts([formatNumericValue(convertedQuantity), convertedUom.trim()])
    : null;

  return convertedDisplay ? `${label}: ${primaryDisplay} (${convertedDisplay})` : `${label}: ${primaryDisplay}`;
};

const OrderFormRow: React.FC<OrderFormRowProps> = memo(function OrderFormRow({
  item,
  orderType,
  dates,
  getItemUpdate,
  handleSingleQuantityChange,
  canSaveOrder,
  isQuantityError,
  quantityError,
  showMessageWhenNoCost,
  currencyCode,
  t,
  predictedDemands,
  handleMagicWandClicked,
  canEditPredictions,
  upsertOrderFormItemUpdate,
  isAlternateItemCardEnabled,
  isParEnabled = false,
}) {
  const current = getItemUpdate(item);
  const theme = useTheme();
  const itemNumber = item.sku;
  const orderableConversionCount = getOrderableConversionCount(item);
  const conversionDisplay = formatConversionDisplay(orderableConversionCount, item.primaryUom, item.orderableUom);
  const costDisplay = formatCostDisplay(item.price, currencyCode, showMessageWhenNoCost);
  const orderLine = formatOrderLine(t('itemCardOrderLabel'), costDisplay, item.orderableUom, conversionDisplay);
  const onOrderLine = formatStockLine(t('itemCardOnOrderLabel'), item.stockOnOrder, item.orderableUom);
  const stockOnHandValue = parseNumberLike(item.stockOnHand);
  const hasOnHandConversion = stockOnHandValue != null
    && orderableConversionCount != null
    && orderableConversionCount > 0
    && Math.abs(orderableConversionCount - 1) >= 0.0001
    && hasText(item.orderableUom);
  const onHandConvertedQuantity = hasOnHandConversion ? stockOnHandValue! / orderableConversionCount! : null;
  // V2 order form convention: orderable UOM quantity is always shown first,
  // with the base/primary UOM quantity shown second, in brackets.
  const onHandLine = formatStockLine(
    t('itemCardOnHandLabel'),
    onHandConvertedQuantity ?? stockOnHandValue,
    hasOnHandConversion ? item.orderableUom : item.primaryUom,
    hasOnHandConversion ? stockOnHandValue : null,
    item.primaryUom,
  );
  const parLine = isParEnabled && item.calculatedParQuantity != null
    ? t('parLevel', { value: item.calculatedParQuantity.toString() })
    : null;

  const ProductInfoCell = () => {
    if (!isAlternateItemCardEnabled) {
      return (
        <TableCell sx={{ maxWidth: '20%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Stack direction='row'
            spacing={6}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}>
            <Box
              sx={{
                width: '100px',
                height: '100px',
                justifyContent: 'center',
                backgroundColor: (item?.thumbnailUrl ?? '') !== '' ? theme.palette.common.white : theme.palette.custom.gray[200],
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #ddd',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                imageRendering: '-webkit-optimize-contrast',
                backgroundImage: `url(${item.thumbnailUrl ? item.thumbnailUrl : ''})`,
              }}
            />
            <Box sx={{ maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Tooltip title={item.name}
                arrow>
                <Typography variant='textXS'
                  display="block"
                  fontWeight={600}>
                  {item.isFrozen ? '❄️ ' : ''}{item.name}
                </Typography>
              </Tooltip>
              <Typography variant='textXS'
                display="block"
                fontWeight={400}>
                {item.sku}
              </Typography>
              <Typography variant='textXS'
                display="block">
                {t('orderableUOM')} {item.orderableUom} ({item.packSize}
                {item.primaryUom})
              </Typography>
              <Typography variant='textXS'
                display="block">
                {t('primaryUOM')} {item.primaryUom}
              </Typography>
              {parLine && (
                <Typography variant='textXS'
                  display="block">
                  {parLine}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Tooltip title={`${t('stockOnHand')}: ${item.stockOnHand}`}
                  arrow>
                  <Chip
                    size="small"
                    color="primary"
                    variant='outlined'
                    label={`${t('stockOnHandABR')} ${item.stockOnHand}`}
                    sx={{
                      mr: 0,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    }}
                  />
                </Tooltip>
                <Tooltip title={`${t('stockOnOrder')}: ${item.stockOnOrder}`}
                  arrow>
                  <Chip
                    size="small"
                    color="warning"
                    variant='outlined'
                    label={`${t('StockOnOrderABR')} ${item.stockOnOrder}`}
                    sx={{
                      mr: 0,
                      borderColor: '#d97706',
                      color: '#d97706',
                    }}
                  />
                </Tooltip>
                {!(showMessageWhenNoCost || item.price === 0) && <Chip
                  size="small"
                  color="primary"
                  variant='outlined'
                  label={applyRoundAndFormat(item.price, currencyCode)}
                  sx={{
                    mr: 0,
                    borderColor: '#0284c7',
                    color: '#0284c7',
                  }}
                />}
              </Box>
            </Box>
          </Stack>
        </TableCell>
      );
    }

    const detailLines = [orderLine, onOrderLine, onHandLine, parLine].filter((line): line is string => hasText(line ?? ''));

    return (
      <TableCell sx={{ maxWidth: '26%', py: 1.5 }}>
        <Stack direction="row"
          spacing={6}
          sx={{
            alignItems: 'center',
            minWidth: 0,
          }}>
          <Box
            sx={{
              position: 'relative',
              width: 104,
              height: 104,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: theme.palette.custom.gray[300],
              backgroundColor: theme.palette.common.white,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: hasText(item.thumbnailUrl) ? theme.palette.common.white : theme.palette.custom.gray[200],
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                imageRendering: '-webkit-optimize-contrast',
                backgroundImage: `url(${hasText(item.thumbnailUrl) ? item.thumbnailUrl : ''})`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                left: 0,
                height: 26,
                borderTop: '1px solid',
                borderColor: theme.palette.custom.gray[300],
                backgroundColor: !hasText(item.thumbnailUrl) ? theme.palette.common.white : theme.palette.custom.gray[200],
              }}
            />
            {hasText(itemNumber) && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 6,
                  bottom: 3,
                  maxWidth: 'calc(100% - 12px)',
                }}
              >
                <Typography
                  variant="textXS"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {itemNumber}
                </Typography>
              </Box>
            )}
          </Box>
          <Stack spacing={0.75}
            sx={{
              minWidth: 0,
              flex: 1,
              justifyContent: 'center',
            }}>
            <Tooltip title={item.name}
              arrow>
              <Typography
                variant="textXS"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.isFrozen ? '❄️ ' : ''}{item.name}
              </Typography>
            </Tooltip>
            {detailLines.map((line) => (
              <Typography
                key={line}
                variant="textXS"
                sx={{
                  display: 'block',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {line}
              </Typography>
            ))}
            <UsageSummaryPill item={item} />
          </Stack>
        </Stack>
      </TableCell>
    );
  };

  // QuantityCell for each date
  const renderQuantityCells = dates.map((dateObj, index) => {
    const date = dateObj.deliveryDate;
    const originalQty = item.quantity[index];
    const updatedQty = current?.dates.find((d) => d.date === date)?.quantity;
    const parQuantityForDate = item.parQuantityApplied?.[index] ?? item.calculatedParQuantity ?? null;
    const isParDefaultValue = isParEnabled && parQuantityForDate != null && (updatedQty === null || (updatedQty == null && originalQty == null));
    const currentValue = updatedQty != null
      ? updatedQty.toString()
      : originalQty != null
        ? originalQty.toString()
        : isParDefaultValue
          ? parQuantityForDate.toString()
          : isParEnabled
            ? ''
            : '0';

    const podItem = predictedDemands.find((x) => x.itemNumber === getInventorySku(item));
    const predictedItem = podItem?.predictedDemandItems?.find(p =>
      isSameDayOrderForm(p.date, date),
    );
    const hasPrediction = predictedItem?.hasPrediction;
    const predictedQuantity = hasPrediction ? predictedItem.quantity : 0;
    const matchesPredictedValue = predictedQuantity.toString() === currentValue;
    const borderColor = hasPrediction && canEditPredictions && matchesPredictedValue ? theme.palette.success[400] : '';

    const predictedDemandQuantity = predictedItem?.predictedDemandQuantity ?? undefined;
    const minStockOnHandQty = predictedItem?.minStockOnHandQty ?? predictedItem?.safetyStock ?? undefined;

    return (
      <TableCell key={`${item.id}-${orderType}-${date}`}
        align="center">
        <QuantityCell
          item={item}
          dateIndex={index}
          key={`quantityCell-${item.id}-${orderType}-${date}`}
          currentValue={currentValue}
          handleSingleQuantityChange={handleSingleQuantityChange}
          canSave={canSaveOrder}
          isError={isQuantityError(item, currentValue)}
          helperText={quantityError(item, currentValue)}
          showPrediction={hasPrediction}
          predictedQuantity={predictedQuantity}
          onMagicWandClick={() => handleMagicWandClicked(predictedQuantity, item, index)}
          canEditPredictions={canEditPredictions}
          borderColor={borderColor}
          predictedDemandQuantity={predictedDemandQuantity}
          minStockOnHandQty={minStockOnHandQty}
          primaryUom={item.primaryUom}
          date={date}
          upsertOrderFormItemUpdate={upsertOrderFormItemUpdate}
          isParEnabled={isParEnabled}
          parQuantity={isParDefaultValue ? parQuantityForDate : undefined}
        />
      </TableCell>
    );
  });

  return (
    <>
      <ProductInfoCell />
      {renderQuantityCells}
    </>
  );
});

export default OrderFormRow;